import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { MoreVertical, Megaphone, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChannelHeader } from '../../services/api';
import { useStore } from '../../store';
import { askBeforeLeaving } from '../../hooks/useLeaveGuard';
import { MOTION } from './chatMotion';

interface ChannelMenuProps {
  header: ChannelHeader;
  onShowMembers: () => void;
  onShowAnnouncers: () => void;
  /** Phone sheet: a 44 px trigger. */
  touch?: boolean;
}

/**
 * No archive item: archive is a Group Manager action. "Manage group"
 * opens that group in Group Manager; the server reports `can_manage_group` false
 * for automatic and private channels, so no extra condition is needed here.
 */
export function ChannelMenu({
  header,
  onShowMembers,
  onShowAnnouncers,
  touch = false,
}: ChannelMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const inSheet = useStore(state => state.chatLayout === 'sheet');
  const setChatSheetOpen = useStore(state => state.setChatSheetOpen);
  return (
    // Non-modal so the menu never writes `pointer-events: none` to the body for
    // the Group Manager drawer's separate Radix copy to re-apply on close.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size={touch ? 'iconLg' : 'icon'}
          variant='ghost'
          aria-label='Conversation menu'
          title='Conversation menu'
        >
          <MoreVertical className='h-3.5 w-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className={MOTION.reduceRadix}>
        <DropdownMenuItem onClick={onShowMembers}>
          <Users className='mr-2 h-4 w-4' />
          Members ({header.member_count})
        </DropdownMenuItem>
        {header.can_edit_announcers && (
          <DropdownMenuItem onClick={onShowAnnouncers}>
            <Megaphone className='mr-2 h-4 w-4' />
            Announcers
          </DropdownMenuItem>
        )}
        {header.can_manage_group && (
          <DropdownMenuItem
            onSelect={() => {
              // Record where we came from so closing the group drawer can
              // return here instead of stranding the person on the list.
              const go = () => {
                navigate(`/group-manager?group=${header.group_id}`, {
                  state: { from: location.pathname + location.search },
                });
                // Below md the sheet covers Group Manager, as it does the chart.
                if (inSheet) setChatSheetOpen(false);
              };
              if (!askBeforeLeaving(go)) go();
            }}
          >
            <Users className='mr-2 h-4 w-4' />
            Manage group
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
